import { z } from "zod";
import { digitsLen } from "@/utils/validation";
import { isLettersOnly } from "@/utils/format";
import { enT, type Translate } from "@/features/onboarding/i18n/translate";

export const req = (msg: string) => z.string().trim().min(1, msg);
export const opt = () => z.string();
export const numAmount = (t: Translate = enT) =>
  z.string().refine((s) => s === "" || (Number.isFinite(Number(s)) && Number(s) >= 0), t("v.amount"));

const digitCount = (s: string) => s.replace(/\D/g, "").length;

const tooLongMsg = (t: Translate, label: string, max: number) => t("v.tooLong", { label, max });

/** Required field of exactly `digits` numeric digits (value may be formatted). */
export const digitsReq = (digits: number, cap: number, label: string, t: Translate = enT) =>
  z.string().trim().min(1, t("v.required", { label })).max(cap, tooLongMsg(t, label, cap))
    .refine((s) => digitsLen(s, digits), t("v.digits", { label, n: digits }));

/** Optional numeric field; when present must be exactly `digits` digits. */
export const digitsOpt = (digits: number, cap: number, label: string, t: Translate = enT) =>
  z.string().max(cap, tooLongMsg(t, label, cap))
    .refine((s) => s === "" || digitsLen(s, digits), t("v.digits", { label, n: digits }));

/** Required numeric field of `min`–`max` digits. */
export const digitsRangeReq = (min: number, max: number, cap: number, label: string, t: Translate = enT) =>
  z.string().trim().min(1, t("v.required", { label })).max(cap, tooLongMsg(t, label, cap))
    .refine((s) => { const d = digitCount(s); return d >= min && d <= max; }, t("v.digitsRange", { label, min, max }));

/** US ZIP field: 5 or 9 digits. */
export const zipField = (required: boolean, cap: number, label: string, t: Translate = enT) => {
  const ok = (s: string) => digitsLen(s, 5) || digitsLen(s, 9);
  return required
    ? z.string().trim().min(1, t("v.required", { label })).max(cap, tooLongMsg(t, label, cap)).refine(ok, t("v.zip", { label }))
    : z.string().max(cap, tooLongMsg(t, label, cap)).refine((s) => s === "" || ok(s), t("v.zip", { label }));
};

/** Required letters-only name field. */
export const lettersReq = (cap: number, label: string, t: Translate = enT) =>
  z.string().trim().min(1, t("v.required", { label })).max(cap, tooLongMsg(t, label, cap))
    .refine(isLettersOnly, t("v.letters", { label }));

/** Optional letters-only name field. */
export const lettersOpt = (cap: number, label: string, t: Translate = enT) =>
  z.string().max(cap, tooLongMsg(t, label, cap))
    .refine((s) => s === "" || isLettersOnly(s), t("v.letters", { label }));

export function trimmed(value?: string | null): string {
  return (value ?? "").trim();
}

export function splitGivenNames(firstName?: string | null, middleName?: string | null) {
  const first = trimmed(firstName);
  const middle = trimmed(middleName);
  if (middle || !first.includes(" ")) return { first, middle };
  const parts = first.split(/\s+/);
  return { first: parts[0], middle: parts.slice(1).join(" ") };
}

export function fileBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const r = String(reader.result);
      const comma = r.indexOf(",");
      resolve(comma >= 0 ? r.slice(comma + 1) : r);
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

export function clean(s: string): string | null {
  const t = s.trim();
  return t.length > 0 ? t : null;
}

export function num(s: string): number | null {
  const t = s.trim();
  if (t.length === 0) return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}
